import { NeedDto } from '@act/shared/data-transfer-objects';
import { useMemo } from 'react';
import { useAppSelector } from 'redux/store-hooks';
import { needsSelectors } from 'redux/slices/needs.slice';
import {
  selectActionById,
  selectNeedUpdateById,
  selectActionsAdapterState,
  selectNeedUpdateAdapterState,
} from 'redux/slices/needs.slice';
import HistoryEntry from './history-entry';

interface NeedHistoryProps {
  need: NeedDto;
  detailMode?: boolean;
  editMode?: boolean;
}

const NeedHistory = ({ need, detailMode, editMode }: NeedHistoryProps) => {
  const history = needsSelectors.useSelectNeedHistoryEntriesByIds(
    need.historyEntryIds
  );

  // Get action IDs and needUpdate IDs from history entries
  const actionIds = useMemo(() => {
    return history
      .filter(h => h.actionId !== undefined)
      .map(h => h.actionId as number);
  }, [history]);

  const needUpdateIds = useMemo(() => {
    return history
      .filter(h => h.needUpdateId !== undefined)
      .map(h => h.needUpdateId as number);
  }, [history]);

  // Get adapter states for building maps
  const actionsAdapterState = useAppSelector(state =>
    selectActionsAdapterState(state)
  );
  const needUpdatesAdapterState = useAppSelector(state =>
    selectNeedUpdateAdapterState(state)
  );

  // Build maps from the adapter states
  const { actionsMap, needUpdatesMap } = useMemo(() => {
    const actions = new Map<number, any>();
    actionIds.forEach(actionId => {
      const mockState = { need: { actions: actionsAdapterState } } as any;
      const action = selectActionById(mockState, actionId);
      if (action) {
        actions.set(actionId, action);
      }
    });

    const updates = new Map<number, any>();
    needUpdateIds.forEach(updateId => {
      const mockState = {
        need: { needUpdates: needUpdatesAdapterState },
      } as any;
      const update = selectNeedUpdateById(mockState, updateId);
      if (update) {
        updates.set(updateId, update);
      }
    });

    return { actionsMap: actions, needUpdatesMap: updates };
  }, [actionIds, needUpdateIds, actionsAdapterState, needUpdatesAdapterState]);

  return (
    <div className="need-history">
      {history.map(h => (
        <div key={`history-${h.id}`} className="entry">
          <HistoryEntry
            need={need}
            historyEntry={h}
            detailMode={detailMode}
            editMode={editMode}
          />
        </div>
      ))}
    </div>
  );
};

export default NeedHistory;
